/**
 * Rebuy Sitewide Sale Price Helper
 *
 * Finds Rebuy price elements, parse prices, apply discount, update DOM.
 * Uses MutationObserver to catch dynamically loaded widgets.
 */

(function() {
  'use strict';

  // Helper function to parse price from text like "$49" or "$49.00" or "Sale price $109.00"
  function parsePrice(priceText) {
    if (!priceText) return 0;
    // Extract just the numeric value, removing currency symbols and text
    const match = priceText.match(/\$?\s*(\d+(?:\.\d{2})?)/);
    return match ? parseFloat(match[1]) : 0;
  }

  // Helper function to format price
  function formatPrice(price) {
    const formatted = price.toFixed(2).replace('.00', '');
    return '$' + formatted;
  }

  // Calculate sale price
  function calculateSalePrice(originalPrice, isSubscription) {
    if (!window.moonjuiceSitewideSale || !window.moonjuiceSitewideSale.enabled) {
      return originalPrice;
    }

    const multiplier = isSubscription
      ? window.moonjuiceSitewideSale.subscriptionMultiplier
      : window.moonjuiceSitewideSale.onetimeMultiplier;

    return originalPrice * multiplier;
  }

  // Process a single price container
  function updatePriceContainer(container) {
    if (!window.moonjuiceSitewideSale || !window.moonjuiceSitewideSale.enabled) {
      return;
    }

    // Skip if already processed
    if (container.hasAttribute('data-sale-processed')) {
      return;
    }

    // Check if this is a subscription hover price widget (different structure)
    const subscribeHoverPrices = container.querySelector('.collection-subscribe-hover-prices');

    if (subscribeHoverPrices) {
      // Handle collection-subscribe-hover-prices structure
      updateSubscribeHoverPrices(subscribeHoverPrices);
      container.setAttribute('data-sale-processed', 'true');
      return;
    }

    // Standard rebuy-money structure
    // Find the price element (not compare-at)
    const priceElement = container.querySelector('.rebuy-money:not(.compare-at)');
    if (!priceElement) return;

    // Get original price text
    const originalText = priceElement.textContent.trim();
    const originalPrice = parsePrice(originalText);

    if (originalPrice === 0) return;

    // Determine if subscription
    const productBlock = container.closest('.rebuy-product-block, .rebuy-addon__item');
    const isSubscription = productBlock &&
      productBlock.querySelector('.subscription-checkbox input[type="checkbox"]:checked');

    // Calculate sale price
    const salePrice = calculateSalePrice(originalPrice, isSubscription);
    const salePriceFormatted = formatPrice(salePrice);

    // Update the price
    priceElement.textContent = salePriceFormatted;

    // Find or create compare-at element
    let compareAtElement = container.querySelector('.rebuy-money.compare-at');

    if (!compareAtElement) {
      // Create compare-at element
      compareAtElement = document.createElement('span');
      compareAtElement.className = 'rebuy-money compare-at';

      // Insert after price element
      if (priceElement.nextSibling) {
        priceElement.parentNode.insertBefore(compareAtElement, priceElement.nextSibling);
      } else {
        priceElement.parentNode.appendChild(compareAtElement);
      }
    }

    // Set strikethrough price
    compareAtElement.innerHTML = '<del>' + formatPrice(originalPrice) + '</del>';

    // Mark as processed
    container.setAttribute('data-sale-processed', 'true');
  }

  function updateSubscribeHoverPrices(container) {
    if (!window.moonjuiceSitewideSale || !window.moonjuiceSitewideSale.enabled) {
      return;
    }

    // Skip if already processed
    if (container.hasAttribute('data-sale-processed')) {
      return;
    }

    // Find regular-price and compare-price elements
    const regularPriceElement = container.querySelector('.regular-price');
    const comparePriceElement = container.querySelector('.compare-price');

    if (!regularPriceElement) return;

    // Get original price text
    const originalText = regularPriceElement.textContent.trim();
    const originalPrice = parsePrice(originalText);

    if (originalPrice === 0) return;

    // This widget shows subscription prices (90% of regular)
    const isSubscription = true;

    // Calculate sale price
    const salePrice = calculateSalePrice(originalPrice, isSubscription);
    const salePriceFormatted = formatPrice(salePrice);

    // Update the regular price
    regularPriceElement.textContent = salePriceFormatted;

    // Update compare price if it exists
    if (comparePriceElement) {
      comparePriceElement.textContent = formatPrice(originalPrice);
    }

    // Mark as processed
    container.setAttribute('data-sale-processed', 'true');
  }

  // Process all Rebuy widgets on the page
  function processAllRebuyWidgets() {
    if (!window.moonjuiceSitewideSale || !window.moonjuiceSitewideSale.enabled) {
      return;
    }

    // Find all rebuy price containers (both types)
    const standardPriceContainers = document.querySelectorAll('.rebuy-product-price');
    const hoverPriceContainers = document.querySelectorAll('p.price');

    // Process standard containers
    standardPriceContainers.forEach(container => {
      try {
        updatePriceContainer(container);
      } catch (error) {
        console.error('[Sitewide Sale] Error updating price:', error);
      }
    });

    // Process hover price containers (only if inside rebuy widgets)
    hoverPriceContainers.forEach(container => {
      // Only process if inside a rebuy widget
      if (container.closest('.rebuy-widget, .rebuy-product-block')) {
        try {
          updatePriceContainer(container);
        } catch (error) {
          console.error('[Sitewide Sale] Error updating hover price:', error);
        }
      }
    });
  }

  // Set up MutationObserver to watch for new Rebuy widgets
  function observeRebuyWidgets() {
    const observer = new MutationObserver((mutations) => {
      let shouldProcess = false;

      mutations.forEach(mutation => {
        // Check if any added nodes contain Rebuy content
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            if (node.classList && (
              node.classList.contains('rebuy-widget') ||
              node.classList.contains('rebuy-product-block') ||
              node.classList.contains('rebuy-product-price')
            )) {
              shouldProcess = true;
            } else if (node.querySelector && node.querySelector('[class*="rebuy"]')) {
              shouldProcess = true;
            }
          }
        });
      });

      if (shouldProcess) {
        // Debounce processing
        clearTimeout(window._rebuyProcessTimeout);
        window._rebuyProcessTimeout = setTimeout(processAllRebuyWidgets, 200);
      }
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Listen for subscription checkbox changes
  function listenForSubscriptionChanges() {
    document.addEventListener('change', function(e) {
      if (e.target.classList && e.target.classList.contains('rebuy-checkbox')) {
        // Find the price container for this product
        const productBlock = e.target.closest('.rebuy-product-block, .rebuy-addon__item');
        if (productBlock) {
          const priceContainer = productBlock.querySelector('.rebuy-product-price');
          if (priceContainer) {
            // Remove processed flag and re-process
            priceContainer.removeAttribute('data-sale-processed');
            updatePriceContainer(priceContainer);
          }
        }
      }
    });
  }

  function init() {
    observeRebuyWidgets();
    listenForSubscriptionChanges();
    processAllRebuyWidgets();

    // Also process after short delays to catch late-loading widgets
    setTimeout(processAllRebuyWidgets, 500);
    setTimeout(processAllRebuyWidgets, 1000);
    setTimeout(processAllRebuyWidgets, 2000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also expose function for manual triggering
  window.processRebuyPricing = processAllRebuyWidgets;
})();
